import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function formatCurrency(value) {
  if (typeof value !== 'number') value = Number(value);
  if (isNaN(value)) return '';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

export default function VisualizationComponent({ viz }) {
  if (!viz) return null;

  // Handle pivot table only (no chart) for pivot_chart
  if (viz.type === 'pivot_chart' && viz.chart_type === 'grouped_bar') {
    // viz.data: [{ customer: "Customer A", "2025-02": 100, ... }, ...]
    // viz.x_axis: ["2025-02", "2025-03", ...]
    // viz.y_axis: ["Customer A", "Customer B", ...]

    // Calculate totals
    const customerTotals = viz.data.map(row =>
      viz.x_axis.reduce((sum, period) => sum + (Number(row[period]) || 0), 0)
    );
    const periodTotals = viz.x_axis.map(period =>
      viz.data.reduce((sum, row) => sum + (Number(row[period]) || 0), 0)
    );
    const grandTotal = customerTotals.reduce((a, b) => a + b, 0);

    return (
      <div style={{ overflowX: 'auto', marginTop: 24 }}>
        <h3>{viz.title}</h3>
        <table className="pivot-table">
          <thead>
            <tr>
              <th>{viz.y_axis_label || 'Customer'}</th>
              {viz.x_axis.map((period, idx) => (
                <th key={idx}>{period}</th>
              ))}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {viz.data.map((row, j) => (
              <tr key={j}>
                <td>{row.customer}</td>
                {viz.x_axis.map((period, i) => (
                  <td key={i}>{formatCurrency(row[period])}</td>
                ))}
                <td><b>{formatCurrency(customerTotals[j])}</b></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              {periodTotals.map((total, i) => (
                <th key={i}><b>{formatCurrency(total)}</b></th>
              ))}
              <th><b>{formatCurrency(grandTotal)}</b></th>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  // Handle grouped bar (pivot) chart
  if (viz.type === 'pivot_chart' && viz.chart_type === 'grouped_bar') {
    // Transform backend data to Recharts format
    // Example backend: { x_axis: ['Jan', 'Feb'], y_axis: ['Customer A', 'Customer B'], data: [[100, 200], [150, 250]] }
    // We want: [{ period: 'Jan', 'Customer A': 100, 'Customer B': 150 }, ...]
    const chartData = viz.x_axis.map((period, i) => {
      const entry = { period };
      viz.y_axis.forEach((customer, j) => {
        entry[customer] = viz.data[j][i];
      });
      return entry;
    });

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c', '#d0ed57', '#8dd1e1', '#d88884'];

    return (
      <div>
        <h3>{viz.title}</h3>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={chartData}>
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip />
            <Legend />
            {viz.y_axis.map((customer, idx) => (
              <Bar key={customer} dataKey={customer} fill={colors[idx % colors.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {/* Pivot Table */}
        <div style={{ overflowX: 'auto', marginTop: 24 }}>
          <table className="pivot-table">
            <thead>
              <tr>
                <th>{viz.x_axis_label || 'Period'}</th>
                {viz.y_axis.map((customer, idx) => (
                  <th key={idx}>{customer}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viz.x_axis.map((period, i) => (
                <tr key={i}>
                  <td>{period}</td>
                  {viz.y_axis.map((customer, j) => (
                    <td key={j}>{viz.data[j][i]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Handle new chart format from backend
  if (viz.type === 'chart' && viz.data && Array.isArray(viz.data)) {
    const chartType = viz.chart_type || 'bar';
    
    if (chartType === 'bar') {
      return (
        <div style={{ marginTop: 24 }}>
          <h3>{viz.title}</h3>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={viz.data}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
    
    if (chartType === 'pie') {
      return (
        <div style={{ marginTop: 24 }}>
          <h3>{viz.title}</h3>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={viz.data}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {viz.data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#a4de6c'][index % 5]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(value)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }
  }

  return null;
} 